import React, { useEffect, useState, useContext } from "react";
import TabsContainer from "./Tabs/TabsContainer";
import DeclarationsList from "../../components/layouts/declarations/DeclarationsList/index";
import DeclarationContext from "../../context/declaration/DeclarationContext";
import ThemeContext from "../../context/theme/ThemeContext";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getQueryParam, updateQueryParam } from "../../router/utils";
import DeclarationPageHeader from "../../components/layouts/declarations/PageHeader/Header";
import { useBeforeunload } from "react-beforeunload";
import PageContainer from "../../components/layouts/PageContainer";
import { getAllNotifications, getNotifications } from "../../api/notifications";
import { AMENDMENT_QUERY_PARAM, CREATION_TIME_QUERY_PARAM } from "../../constants/GlobalConstants";
import UnsavedChangesModal from "../../components/modals/UnsavedChangesModal";
import EmptyPageMessage from "../../components/common/EmptyPageMessage";
import declarationsErrorIllust from "../../assets/images/declarations-error-illust.png";
import NewDeclarationModal from "../../components/layouts/declarations/NewDeclarationModal/NewDeclarationModal";

const DeclarationLayout = () => {
	/*
	 * If isAmendment is true, AMENDMENT_QUERY_PARAM mode will be enabled. It can only be enabled for successfully submitted declarations
	 * If enabled, elements in "amendment-grouping.json" will be showing instead of the regular "header-grouping.json" and "items-grouping.json"
	 */
	const [isAmendment, setIsAmendment] = useState(false);
	const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
	const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
	const [isNewDeclarationModalOpen, setIsNewDeclarationModalOpen] = useState(false);
	const [loadingAction, setLoadingAction] = useState();
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);
	const { state, setHasUnsavedChanges, setDeclaration, listDeclarations, setNotifications } =
		useContext(DeclarationContext);
	const { declaration } = state;
	const { declarations, declarationsLoading } = state;
	const isFinalSupplementary = declaration?.jdeclaration?.data?.declaration?.service === "finalSupplementary";
	const { withTheme } = useContext(ThemeContext);
	const { t } = useTranslation();

	useBeforeunload((event) => {
		if (state.hasUnsavedChanges) {
			event.preventDefault();
		}
	});

	useEffect(() => {
		if (!declarationsLoading && !state.declarations.length) {
			listDeclarations();
		}

		return function cleanup() {
			setHasUnsavedChanges(false);
		};
	}, []);

	const findDeclarationAndSelect = async (creationTime) => {
		const declaration = declarations.find((dec) => dec.creationTime === creationTime);
		if (declaration) {
			setDeclaration({
				declaration: declaration.jdeclaration.data.declaration,
				metadata: {
					conversationId: declaration.conversationId,
					functionalReferenceID: declaration.functionalReferenceID,
					creationTime: declaration.creationTime,
					status: declaration.status,
					description: declaration.jdeclaration.meta.description,
				},
			});

			setHasUnsavedChanges(false);

			setIsNotificationsLoading(true);

			// TODO: Cancel request when another declaration is selected before notifications are loaded
			const [notifications, allNotifications] = await Promise.all([
				getNotifications(declaration?.conversationId),
				getAllNotifications(declaration?.functionalReferenceID),
			]);

			const notificationsFiltered = [...notifications, ...allNotifications].filter(
				(value, index, self) =>
					index === self.findIndex((notification) => notification._id === value._id)
			);

			setNotifications(notificationsFiltered);
			setIsNotificationsLoading(false);
		}
	};

	// Listen to URL changes and update the selected declaration from the creationTime parameter
	useEffect(() => {
		const eventHandler = () => {
			if (state.declarationMetadata?.creationTime !== getQueryParam(CREATION_TIME_QUERY_PARAM)) {
				findDeclarationAndSelect(getQueryParam(CREATION_TIME_QUERY_PARAM));
			}

			const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM) === "true";
			setIsAmendment(isAmendment);
		};

		window.addEventListener("popstate", eventHandler);

		return function cleanup() {
			window.removeEventListener("popstate", eventHandler);
		};
	}, [state.declaration, declarations]);

	useEffect(() => {
		findDeclarationAndSelect(getQueryParam(CREATION_TIME_QUERY_PARAM));
	}, [declarations]);

	useEffect(() => {
		if (isAmendment && !declaration.amendment) {
			updateQueryParam(AMENDMENT_QUERY_PARAM, "");
		}
	}, [isAmendment]);

	return (
		<PageContainer title={t("declarations")} style={{ padding: 0 }}>
			<NewDeclarationModal
				isOpen={isNewDeclarationModalOpen}
				onSubmit={() => listDeclarations()}
				onClose={() => setIsNewDeclarationModalOpen(false)}
			/>

			<UnsavedChangesModal hasUnsavedChanges={state.hasUnsavedChanges} />

			<div style={{ display: "flex", width: "100%" }}>
				<DeclarationsList
					declarationsLoading={declarationsLoading}
					isExpanded={isDrawerExpanded}
					onAddClick={() => setIsNewDeclarationModalOpen(true)}
					setIsExpanded={setIsDrawerExpanded}
				/>
				<div
					style={{
						transition: "0.3s",
						width: `calc(100% - ${isDrawerExpanded ? 320 : 64}px)`,
						paddingLeft: 40,
						paddingRight: 40,
					}}
				>
					<DeclarationPageHeader
						selectedTabIndex={selectedTabIndex}
						isAmendment={isAmendment}
						disableSearch={isFinalSupplementary}
						loadingAction={loadingAction}
						setLoadingAction={setLoadingAction}
					/>

					{!declarationsLoading && (
						<>
							{!state.declarations.length ? (
								<EmptyPageMessage
									image={declarationsErrorIllust}
									text={t("noDeclarationsYet")}
									actionButtonLabel={t("addNewDeclaration")}
									onActionButtonClick={() =>
										setIsNewDeclarationModalOpen(true)
									}
								/>
							) : state.declaration ? (
								<TabsContainer
									selectedTabIndex={selectedTabIndex}
									setSelectedTabIndex={setSelectedTabIndex}
									isAmendment={isAmendment}
									isFinalSupplementary={isFinalSupplementary}
									isNotificationsLoading={isNotificationsLoading}
								/>
							) : (
								<div
									style={{ paddingTop: 92 }}
									className={withTheme("empty-page-text")}
								>
									{t("selectDeclaration")}
								</div>
							)}
						</>
					)}

					{declarationsLoading && (
						<div className={withTheme("declarations-loading-container")}>
							<Spinner animation="border" role="status" />
						</div>
					)}
				</div>
			</div>
		</PageContainer>
	);
};

export default DeclarationLayout;
